import { ApiError, OpenAPIConfig, ApiNoSql } from '../generated-api-clients/no-sql';
import { ApiRequestOptions } from '../generated-api-clients/no-sql/core/ApiRequestOptions';
import { ApiResult } from '../generated-api-clients/no-sql/core/ApiResult';
import {
	FunctionComponent,
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';

type RequestFunction<T> = (client: ApiNoSql) => Promise<T>;

type ApiNoSqlContextType = {
	client: ApiNoSql | undefined;
	error: ApiError | undefined;
	performRequest: <T>(requestFunction: RequestFunction<T>) => Promise<T>;
};

const ApiNoSqlContext = createContext<ApiNoSqlContextType>({
	client: new ApiNoSql(), // This should be a real instance or null initially
	error: undefined,
	// Trailing comma is required in `<T,>` to avoid being recognized as JSX by the TS parser
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	performRequest: async <T,>() => {
		throw new Error('performRequest function not implemented');
	}
});

export const ApiNoSqlProvider: FunctionComponent<PropsWithChildren<{ config?: Partial<OpenAPIConfig> }>> = ({
	children,
	config
}) => {
	const [error, setError] = useState<ApiError | undefined>(undefined);
	const [client, setClient] = useState<ApiNoSql | undefined>(undefined);

	useEffect(() => {
		if (!client && config) {
			setClient(new ApiNoSql(config));
		}
	}, [config, client]);
	// Wrap the request function in a useCallback to prevent unnecessary re-renders.
	// This is especially important if the request function is passed as a prop to a child component.
	// Trailing comma is required in `<T,>` to avoid being recognized as JSX by the TS parser
	const performRequest = useCallback(
		<T,>(requestFunction: RequestFunction<T>): Promise<T> => {
			if (client === undefined) {
				throw new Error('API client is not initialized');
			}
			return requestFunction(client).catch((error: unknown) => {
				const processedError =
					error instanceof ApiError
						? error
						: new ApiError(
								{
									method: 'GET',
									url: client ? client.request.config.BASE : ''
								} as ApiRequestOptions,
								{
									ok: false,
									status: 0,
									statusText:
										'An unexpected error occurred. Actual method unknown, but assumed to be GET (due to type-safety enforcement).',
									url: '',
									body: undefined
								} as ApiResult,
								'An unexpected error occurred. Actual method unknown, but assumed to be GET (due to type-safety enforcement).'
						  );
				console.log('ApiNoSqlContext -> performRequest() -> ApiError:', processedError);
				setError(processedError);
				throw processedError;
			});
		},
		[client]
	);

	const contextValue = useMemo(
		() => ({
			client,
			error,
			performRequest
		}),
		[error, performRequest, client]
	);

	return <ApiNoSqlContext.Provider value={contextValue}>{children}</ApiNoSqlContext.Provider>;
};

export const useApiNoSqlContext = (): ApiNoSqlContextType => {
	const context = useContext(ApiNoSqlContext);
	if (context === undefined) {
		throw new Error('useApiNoSqlContext must be used within a ApiNoSqlContextProvider');
	}
	return context;
};
